import { skipToken } from "@reduxjs/toolkit/query";
import { asPlaybookId } from "@repo/app-types/entities/playbook";
import { asTeammateId } from "@repo/app-types/entities/teammate";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconExternalLink } from "@tabler/icons-react";
import { type FC, useState } from "react";
import { Navigate, useParams, useSearchParams } from "react-router";
import {
  useAllTrainingParticipantsForPlaybookTeammateQuery,
  useGenerateInterviewUrlForTrainingParticipantMutation,
} from "../../api/entities/trainingParticipant.js";
import { appPaths } from "../../app/appPaths.js";
import { FullHeightPageContent } from "../../components/layout/pageLayout.js";
import { useAppSelector } from "../../hooks/hook.js";
import { selectTeammate } from "../teammate/teammateSlice.js";
import { TrainingParticipantDetail } from "./training/trainingParticipantDetail.js";

export interface TrainingTeammatePageTrainingProps {
  // ...
}

export const TrainingTeammatePageTraining: FC<
  TrainingTeammatePageTrainingProps
> = (props) => {
  const {
    playbookId: playbookIdParam,
    teammateId: teammateIdParam,
    participantId,
  } = useParams();

  const authenticatedTeammateId = useAppSelector(selectTeammate)?.id;

  const [searchParams] = useSearchParams();
  const [initialChapterId] = useState(
    () => searchParams.get("chapterId") ?? undefined,
  );

  const playbookId = playbookIdParam
    ? asPlaybookId(playbookIdParam)
    : undefined;

  const teammateId = teammateIdParam
    ? asTeammateId(teammateIdParam)
    : undefined;

  const { data: participants, isLoading } =
    useAllTrainingParticipantsForPlaybookTeammateQuery(
      playbookId && teammateId ? { playbookId, teammateId } : skipToken,
    );

  const [generateInterviewUrl, { isLoading: isGeneratingInterviewUrl }] =
    useGenerateInterviewUrlForTrainingParticipantMutation();

  if (!playbookId) {
    return <Navigate to={appPaths.playbooks.build()} replace />;
  }

  if (!teammateId) {
    return <Navigate to={appPaths.playbook.build({ playbookId })} replace />;
  }

  const overviewHref = appPaths.teammate.build({ playbookId, teammateId });

  if (!participantId) {
    return <Navigate to={overviewHref} replace />;
  }

  const participant = participants?.find((p) => p.id === participantId);

  const onOpenTrainingClick = async () => {
    if (!participantId) {
      return;
    }

    const { interviewUrl } = await generateInterviewUrl({
      participantId,
    }).unwrap();

    window.open(interviewUrl, "_blank", "noopener,noreferrer");
  };

  return (
    // Declared full height: the session viewer fills the page shell's bounded
    // scroll region with its own header row + scrolling transcript.
    <FullHeightPageContent>
      <div className="grid h-full w-full grid-cols-1 grid-rows-[auto_1fr]">
        <div className="w-full max-w-4xl place-self-center px-3">
          {teammateId === authenticatedTeammateId ? (
            <div className="flex min-h-[50px] items-center justify-end border-b border-border-light bg-background py-1">
              <Button
                variant={ButtonVariant.Secondary}
                size={Size.MEDIUM}
                icon={<IconExternalLink className="size-4 shrink-0" />}
                isLoading={isGeneratingInterviewUrl}
                onClick={onOpenTrainingClick}
              >
                Open training
              </Button>
            </div>
          ) : null}
        </div>

        <div className="relative min-h-0">
          {!participant && isLoading ? (
            <div className="p-6">
              <DefaultSkeleton />
            </div>
          ) : null}

          {!participant && participants ? (
            <div className="p-6 text-sm text-red-700">Training not found.</div>
          ) : null}

          {participant ? (
            <TrainingParticipantDetail
              participant={participant}
              initialChapterId={initialChapterId}
            />
          ) : null}
        </div>
      </div>
    </FullHeightPageContent>
  );
};
